import { convertTimestampToDateTime } from "../utils"
import { Button } from "@/components/ui/button"
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormMessage,
} from "@/components/ui/form"
import { Textarea } from "@/components/ui/textarea"
import { Loader } from "lucide-react"
import { useForm } from "react-hook-form"
import { z } from "zod"
import { zodResolver } from "@hookform/resolvers/zod"
import { useState } from "react"
import { ApplicationComment } from "@/api/applicationQueries"

const formSchema = z.object({
    comment: z.string().min(2).max(200),
})

interface CommentsSectionProps {
    comments: ApplicationComment[]
    onAddComment: (comment: string) => void
    isAddCommentPending: boolean
    userRole: string
}

export function CommentsSection({
    comments,
    onAddComment,
    isAddCommentPending,
    userRole,
}: CommentsSectionProps) {
    const [showCommentForm, setShowCommentForm] = useState(false)
    const form = useForm<z.infer<typeof formSchema>>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            comment: "",
        },
    })

    function onSubmit(values: z.infer<typeof formSchema>) {
        onAddComment(values.comment)
        form.reset()
        setShowCommentForm(false)
    }

    return (
        <section className="bg-gray-100 border border-input flex flex-col gap-4 rounded-md p-4 w-full">
            {userRole === "admin" && (
                <>
                    {showCommentForm && (
                        <section className="flex flex-col gap-2 transition-all duration-300 ease-out transform origin-bottom animate-in fade-in slide-in-from-bottom-4">
                            <h3 className="font-bold">Add Comment</h3>
                            <Form {...form}>
                                <form
                                    onSubmit={form.handleSubmit(onSubmit)}
                                    className="space-y-2"
                                >
                                    <FormField
                                        control={form.control}
                                        name="comment"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormControl>
                                                    <Textarea
                                                        placeholder="Add your comment here..."
                                                        className="focus-visible:ring-0 focus-visible:ring-offset-0"
                                                        {...field}
                                                    />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                    <div className="flex gap-2 justify-end">
                                        <Button
                                            type="button"
                                            variant="outline"
                                            onClick={() => {
                                                setShowCommentForm(false)
                                                form.reset()
                                            }}
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            type="submit"
                                            disabled={isAddCommentPending}
                                            className="flex justify-center items-center gap-2"
                                        >
                                            <span>Add Comment</span>
                                            {isAddCommentPending && (
                                                <Loader
                                                    className="animate-spin"
                                                    size={14}
                                                />
                                            )}
                                        </Button>
                                    </div>
                                </form>
                            </Form>
                        </section>
                    )}
                </>
            )}
            <section className="flex flex-col gap-2">
                <div className="flex justify-between items-end">
                    <h3 className="font-bold">Admin Comments</h3>
                    {!showCommentForm && userRole === "admin" && (
                        <Button
                            onClick={() => setShowCommentForm(true)}
                            className="w-fit underline text-app-defaultLight h-8"
                            variant="link"
                        >
                            New Comment
                        </Button>
                    )}
                </div>
                {comments?.length ? (
                    <div className="space-y-4">
                        {comments.map((comment, index) => (
                            <div
                                key={index}
                                className="p-4 flex flex-col gap-2 rounded-lg border border-input bg-white shadow-sm"
                            >
                                <div className="flex justify-between items-center text-sm text-muted-foreground">
                                    <span className="font-medium text-foreground">
                                        {comment.userName || "Admin User"}
                                    </span>
                                    <time className="text-xs">
                                        {convertTimestampToDateTime(
                                            comment.date
                                        )}
                                    </time>
                                </div>
                                <p className="text-sm">{comment.body}</p>
                            </div>
                        ))}
                    </div>
                ) : (
                    <p className="text-sm text-muted-foreground">
                        No comments available
                    </p>
                )}
            </section>
        </section>
    )
}
